// =============================================================================
// createHousehold.ts — create a brand-new, empty household (v0.2.25)
// =============================================================================
//
// This replaces `setupHouseholdForCole` for the no-invite-code signup path.
// Where setupHouseholdForCole seeds the Mullinax family (Mia/Harper, profile,
// target schools, 12 scholarships, 25 AI-discovery inbox items), this creates
// a GENERIC, EMPTY household:
//   - the household document, named by the creator
//   - the creator's parent member doc
//   - one or more bare applicant docs, named by the creator
//   - the three settings docs (queue_weights, notification_prefs,
//     discovery_prefs) with sensible defaults — every household needs these
//
// It seeds NO scholarships, NO inbox items, NO profiles, NO target schools.
// Each applicant is a bare doc; the profile is filled in later via the
// Profile page (which already tolerates a missing profile), consistent with
// the v0.2.24 "Add applicant" behavior.
//
// Batch splitting: writes are split into two sequential batches for the same
// reason setupHouseholdForCole does it — Firestore security rules evaluate
// each write against the PRE-batch committed state. The applicant/settings
// rules call isParent(), which reads members/{uid}. So the parent member doc
// must be committed (batch 1) before applicant/settings writes (batch 2) are
// attempted, or those writes fail the rules.
//
// Idempotency: if the user already belongs to a household, this returns that
// household's id without creating anything — guards against a double-submit
// or a user reaching the create flow when they shouldn't.
// =============================================================================

import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  where,
  writeBatch,
  limit,
} from 'firebase/firestore';
import { db } from '../firebase';
import type { User } from 'firebase/auth';
import { DISCOVERY_LINKS_VERSION, seedDefaultLinksIntoBatch } from './discoveryLinks';

function genId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

export interface CreateHouseholdInput {
  user: User;
  /** Household display name, e.g. "Smith Family". */
  householdName: string;
  /** The creating parent's display name. */
  parentName: string;
  /** One or more applicant display names. At least one is required. */
  applicantNames: string[];
}

export interface CreateHouseholdResult {
  householdId: string;
  applicantIds: string[];
  /** True if the household already existed and nothing new was created. */
  alreadyExisted: boolean;
}

/**
 * Create a new empty household for a user who signed up without an invite
 * code. See file header for full behavior.
 */
export async function createHousehold(
  input: CreateHouseholdInput
): Promise<CreateHouseholdResult> {
  if (!db) throw new Error('Firestore not configured');
  // Capture into a non-null local: TypeScript loses the `!db` narrowing
  // across `await` boundaries, so `db` alone is `Firestore | null` later on.
  const fs = db;

  const { user } = input;
  const householdName = input.householdName.trim() || 'My Household';
  const parentName =
    input.parentName.trim() || (user.email ?? '').split('@')[0] || 'Parent';
  const applicantNames = input.applicantNames
    .map((n) => n.trim())
    .filter((n) => n.length > 0);
  if (applicantNames.length === 0) {
    throw new Error('At least one applicant is required to create a household.');
  }

  // --- Idempotency guard: does this user already have a household? ---
  const existing = await getDocs(
    query(
      collection(fs, 'households'),
      where('member_uids', 'array-contains', user.uid),
      limit(1)
    )
  );
  if (!existing.empty) {
    const hh = existing.docs[0];
    const applicantsSnap = await getDocs(
      collection(fs, 'households', hh.id, 'applicants')
    );
    return {
      householdId: hh.id,
      applicantIds: applicantsSnap.docs.map((d) => d.id),
      alreadyExisted: true,
    };
  }

  // --- IDs ---
  const householdId = genId('hh');
  const applicantIds = applicantNames.map(() => genId('app'));
  const now = serverTimestamp();

  // --- Batch 1: household doc + parent member doc ---
  // Once this commits, isParent() returns true for this user and the batch 2
  // writes will pass the security rules.
  const batch1 = writeBatch(db);

  batch1.set(doc(fs, 'households', householdId), {
    household_id: householdId,
    name: householdName,
    created_by: user.uid,
    member_uids: [user.uid],
    created_at: now,
    updated_at: now,
    archived: false,
    settings_initialized: true,
    // v0.2.26: the default discovery links are seeded in batch2 below, so
    // this household is already initialized — the legacy backfill never runs.
    discovery_links_initialized: true,
    // v0.2.50: records which default-link set was seeded, so defaults added
    // in later versions can be backfilled (see discoveryLinks.ts).
    discovery_links_version: DISCOVERY_LINKS_VERSION,
  });

  batch1.set(doc(fs, 'households', householdId, 'members', user.uid), {
    user_id: user.uid,
    email: user.email ?? '',
    display_name: parentName,
    role: 'parent',
    applicant_id: null,
    invited_by: null,
    invited_at: now,
    accepted_at: now,
    status: 'active',
    last_active_at: now,
    archived: false,
    email_preferences: { deadline_reminders: true },
  });

  await batch1.commit();

  // --- Batch 2: applicants + settings ---
  const batch2 = writeBatch(db);

  applicantNames.forEach((name, i) => {
    batch2.set(doc(fs, 'households', householdId, 'applicants', applicantIds[i]), {
      applicant_id: applicantIds[i],
      linked_user_id: null,
      display_name: name,
      created_at: now,
      updated_at: now,
      archived: false,
    });
  });

  // The three settings docs — generic defaults every household needs.
  batch2.set(doc(fs, 'households', householdId, 'settings', 'queue_weights'), {
    w_deadline: 0.3,
    w_amount: 0.3,
    w_fit: 0.25,
    w_effort: 0.15,
    primary_tier_boost_points: 5,
    backup_tier_boost_points: 2,
    updated_at: now,
    updated_by: user.uid,
  });
  batch2.set(doc(fs, 'households', householdId, 'settings', 'notification_prefs'), {
    email_reminders_enabled: true,
    reminder_days_before: [30, 14, 7, 3, 1],
    morning_digest_enabled: false,
    digest_send_time_local: '06:00',
    timezone: 'America/New_York',
    updated_at: now,
  });
  batch2.set(doc(fs, 'households', householdId, 'settings', 'discovery_prefs'), {
    daily_check_enabled: true,
    daily_check_time_local: '05:00',
    timezone: 'America/New_York',
    max_inbox_items_per_day: 25,
    email_digest_of_new_items: false,
    ai_discovery_reminder_cadence_days: 30,
    discovery_sprint_months: [9, 1, 9],
    updated_at: now,
  });

  // v0.2.26: seed the default scholarship-aggregator links. The household doc
  // above already has discovery_links_initialized: true and the current
  // discovery_links_version, so neither backfill in discoveryLinks.ts runs
  // for this new household.
  seedDefaultLinksIntoBatch(fs, batch2, householdId, user.uid, now);

  await batch2.commit();

  return { householdId, applicantIds, alreadyExisted: false };
}
